import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { supabase } from '@/lib/supabase/client'
import { fetchAllRows } from '@/lib/supabase/fetch-all-rows'
import { forEachIdChunk } from '@/lib/supabase/id-chunks'
import { searchWords } from '@/lib/supabase/search-filter'
import type { Generation } from './generations'
import type { Tag } from './tags'

// =============================================================================
// VTO Look Types
// =============================================================================

type VTOLookRow = Database['public']['Tables']['vto_looks']['Row']

/**
 * VTO Look with current generation and author profile.
 * VTO Looks are domain entities; generations are their versions/transformations.
 * current_generation embeds the full Generation including its author profile.
 */
export interface VTOLook extends VTOLookRow {
  profiles: {
    id: string
    email: string
    full_name: string | null
    avatar_url: string | null
  } | null
  approval_profile: {
    id: string
    full_name: string | null
  } | null
  current_generation: Generation | null
  vto_look_tags: { tags: Tag }[]
}

export interface VTOLookFilterOptions {
  collections: string[]
  genders: string[]
  main_groups: string[]
  sub_groups: string[]
  categories: string[]
}

const PAGE_SIZE = 16
const MIN_SEARCH_WORD_LENGTH = 2

export interface VTOLookFilters {
  authorId?: string | null
  modelId?: string | null
  searchQuery?: string | null
  tagIds?: string[] | null
  collection?: string | null
  gender?: string | null
  mainGroup?: string | null
  subGroup?: string | null
  category?: string | null
  approvalStatus?: 'approved' | 'denied' | 'pending' | null
}

/**
 * Check if any product-based filters are active.
 */
function hasProductFilters(filters?: VTOLookFilters): boolean {
  return !!(
    filters?.collection ||
    filters?.gender ||
    filters?.mainGroup ||
    filters?.subGroup ||
    filters?.category
  )
}

/**
 * Build the select string for VTO looks queries.
 * When product filters are active, includes an !inner join to org_products
 * through the vto_look_products junction table.
 */
const BASE_SELECT = `
  *,
  profiles!author(id, email, full_name, avatar_url),
  approval_profile:profiles!approved_by(id, full_name),
  current_generation:generations!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
  vto_look_tags(tags(id, name, color))
`

const PRODUCT_FILTER_SELECT = `
  *,
  profiles!author(id, email, full_name, avatar_url),
  approval_profile:profiles!approved_by(id, full_name),
  current_generation:generations!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
  vto_look_tags(tags(id, name, color)),
  org_products!inner()
`

// Completed-only variant for picker dialogs: the !inner join drops looks whose
// current generation is missing or unfinished.
const COMPLETED_SELECT = `
  *,
  profiles!author(id, email, full_name, avatar_url),
  approval_profile:profiles!approved_by(id, full_name),
  current_generation:generations!inner!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
  vto_look_tags(tags(id, name, color))
`

/**
 * Apply basic VTO look filters (author, labels, search, approval) to any
 * Supabase query builder. Works with both select and update queries.
 */
function applyBasicFilters<
  T extends {
    eq: (col: string, val: string) => T
    is: (col: string, val: null) => T
    ilike: (col: string, val: string) => T
    contains: (col: string, val: string[]) => T
  },
>(query: T, filters?: VTOLookFilters): T {
  let q = query

  if (filters?.authorId) {
    q = q.eq('author', filters.authorId)
  }

  // Every selected label must be present.
  if (filters?.tagIds && filters.tagIds.length > 0) {
    q = q.contains('tag_ids', filters.tagIds)
  }

  if (filters?.modelId) {
    q = q.eq('model_id', filters.modelId)
  }

  if (filters?.searchQuery) {
    for (const word of searchWords(
      filters.searchQuery,
      MIN_SEARCH_WORD_LENGTH
    )) {
      q = q.ilike('name', `%${word}%`)
    }
  }

  if (filters?.approvalStatus) {
    if (filters.approvalStatus === 'pending') {
      q = q.is('approval_status', null)
    } else {
      q = q.eq('approval_status', filters.approvalStatus)
    }
  }

  return q
}

/**
 * Apply product-based filters via dot-notation on the !inner joined org_products.
 */
function applyProductFilters<
  T extends {
    eq: (col: string, val: string) => T
  },
>(query: T, filters?: VTOLookFilters): T {
  let q = query

  if (filters?.collection) {
    q = q.eq('org_products.collection', filters.collection)
  }
  if (filters?.gender) {
    q = q.eq('org_products.gender', filters.gender)
  }
  if (filters?.mainGroup) {
    q = q.eq('org_products.main_group', filters.mainGroup)
  }
  if (filters?.subGroup) {
    q = q.eq('org_products.sub_group', filters.subGroup)
  }
  if (filters?.category) {
    q = q.eq('org_products.category', filters.category)
  }

  return q
}

/** The list, count, id and export queries share this filter chain. */
function applyLookFilters<
  T extends {
    eq: (col: string, val: string) => T
    is: (col: string, val: null) => T
    ilike: (col: string, val: string) => T
    contains: (col: string, val: string[]) => T
  },
>(query: T, filters?: VTOLookFilters): T {
  const q = applyBasicFilters(query, filters)
  return hasProductFilters(filters) ? applyProductFilters(q, filters) : q
}

async function updateLooksById(
  lookIds: string[],
  payload: Database['public']['Tables']['vto_looks']['Update'],
  errorMessage: string
): Promise<void> {
  await forEachIdChunk(lookIds, async (chunk) => {
    const { error } = await supabase
      .from('vto_looks')
      .update(payload)
      .in('id', chunk)

    if (error) {
      throw new Error(`${errorMessage}: ${error.message}`, { cause: error })
    }
  })
}

export const vtoLook = {
  findOne: (lookId: string) =>
    queryOptions({
      queryKey: ['vto-look', lookId],
      queryFn: async (): Promise<VTOLook | null> => {
        if (!lookId || lookId.trim() === '') {
          return null
        }

        const { data, error } = await supabase
          .from('vto_looks')
          .select(BASE_SELECT)
          .eq('id', lookId)
          .is('deleted_at', null)
          .maybeSingle()

        if (error) {
          throw new Error(`Failed to fetch VTO look: ${error.message}`, {
            cause: error,
          })
        }

        return data as VTOLook | null
      },
    }),

  findManyByWorkspace: (
    workspaceId: string,
    filters?: VTOLookFilters,
    options?: { pageSize?: number }
  ) =>
    infiniteQueryOptions({
      queryKey: [
        'vto-looks',
        workspaceId,
        filters ?? {},
        options?.pageSize ?? PAGE_SIZE,
      ],
      queryFn: async ({ pageParam = 0, signal }): Promise<VTOLook[]> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch VTO looks without valid workspace ID'
          )
        }

        const pageSize = options?.pageSize ?? PAGE_SIZE
        const from = pageParam * pageSize
        const to = from + pageSize - 1

        // Use !inner join when product filters are active
        const selectStr = hasProductFilters(filters)
          ? PRODUCT_FILTER_SELECT
          : BASE_SELECT

        let query = supabase
          .from('vto_looks')
          .select(selectStr)
          .eq('workspace_id', workspaceId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .range(from, to)

        query = applyLookFilters(query, filters)

        const { data, error } = await query.abortSignal(signal)

        if (error) {
          throw new Error(`Failed to fetch VTO looks: ${error.message}`, {
            cause: error,
          })
        }

        return (data || []) as unknown as VTOLook[]
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const pageSize = options?.pageSize ?? PAGE_SIZE
        return lastPage.length === pageSize ? allPages.length : undefined
      },
    }),

  /**
   * How many looks match the filters, counted without fetching them. Shares
   * the list's key prefix so refreshing the list refreshes the count.
   */
  countByWorkspace: (workspaceId: string, filters?: VTOLookFilters) =>
    queryOptions({
      queryKey: ['vto-looks', workspaceId, 'count', filters ?? {}],
      queryFn: async ({ signal }): Promise<number> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot count VTO looks without valid workspace ID'
          )
        }

        let query = supabase
          .from('vto_looks')
          .select(
            hasProductFilters(filters) ? 'id, org_products!inner()' : 'id',
            { count: 'exact', head: true }
          )
          .eq('workspace_id', workspaceId)
          .is('deleted_at', null)

        query = applyLookFilters(query, filters)

        const { count, error } = await query.abortSignal(signal)

        if (error) {
          throw new Error(`Failed to count VTO looks: ${error.message}`, {
            cause: error,
          })
        }

        return count ?? 0
      },
    }),

  /**
   * Get distinct filter options for VTO looks in a workspace.
   * Uses RPC to get DISTINCT values server-side (same pattern as org_products).
   */
  findFilterOptions: (workspaceId: string) =>
    queryOptions({
      queryKey: ['vto-looks', 'filters', workspaceId],
      queryFn: async ({ signal }): Promise<VTOLookFilterOptions> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch VTO look filters without valid workspace ID'
          )
        }

        const { data, error } = await supabase
          .rpc('get_vto_look_filter_options', { p_workspace_id: workspaceId })
          .abortSignal(signal)

        if (error) {
          throw new Error(
            `Failed to fetch VTO look filters: ${error.message}`,
            { cause: error }
          )
        }

        const defaults: VTOLookFilterOptions = {
          collections: [],
          genders: [],
          main_groups: [],
          sub_groups: [],
          categories: [],
        }

        return data ? (data as unknown as VTOLookFilterOptions) : defaults
      },
      staleTime: 5 * 60 * 1000,
    }),

  /**
   * Looks whose current generation is a completed image, for picker dialogs.
   */
  findManyCompletedByWorkspace: (
    workspaceId: string,
    options?: { searchQuery?: string | null }
  ) =>
    infiniteQueryOptions({
      queryKey: [
        'vto-looks',
        workspaceId,
        'completed',
        options?.searchQuery ?? null,
        PAGE_SIZE,
      ],
      queryFn: async ({ pageParam = 0, signal }): Promise<VTOLook[]> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch VTO looks without valid workspace ID'
          )
        }

        const from = pageParam * PAGE_SIZE
        const to = from + PAGE_SIZE - 1

        const query = supabase
          .from('vto_looks')
          .select(COMPLETED_SELECT)
          .eq('current_generation.status', 'completed')
          .not('current_generation.storage_path', 'is', null)
          .eq('workspace_id', workspaceId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })

        const { data, error } = await applyBasicFilters(query, {
          searchQuery: options?.searchQuery,
        })
          .range(from, to)
          .abortSignal(signal)

        if (error) {
          throw new Error(`Failed to fetch VTO looks: ${error.message}`, {
            cause: error,
          })
        }

        return (data || []) as unknown as VTOLook[]
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.length === PAGE_SIZE ? allPages.length : undefined,
    }),

  /**
   * Ids of every look matching the filters, for actions on a select-all
   * selection and for updates the catalogue filters cannot scope directly.
   */
  fetchIdsByWorkspace: async (
    workspaceId: string,
    filters?: VTOLookFilters
  ): Promise<string[]> => {
    if (!workspaceId || workspaceId.trim() === '') {
      return []
    }

    const rows = await fetchAllRows((from, to) => {
      let query = supabase
        .from('vto_looks')
        .select(hasProductFilters(filters) ? 'id, org_products!inner()' : 'id')
        .eq('workspace_id', workspaceId)
        .is('deleted_at', null)

      query = applyLookFilters(query, filters)

      return query
        .order('id')
        .range(from, to)
        .overrideTypes<{ id: string }[], { merge: false }>()
    }, 'Failed to fetch VTO look ids')

    return rows.map((row) => row.id)
  },

  fetchAllByWorkspace: async (
    workspaceId: string,
    filters?: VTOLookFilters
  ): Promise<VTOLook[]> => {
    if (!workspaceId || workspaceId.trim() === '') {
      return []
    }

    return fetchAllRows((from, to) => {
      let query = supabase
        .from('vto_looks')
        .select(
          hasProductFilters(filters) ? PRODUCT_FILTER_SELECT : BASE_SELECT
        )
        .eq('workspace_id', workspaceId)
        .is('deleted_at', null)

      query = applyLookFilters(query, filters)

      return query
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to)
        .overrideTypes<VTOLook[], { merge: false }>()
    }, 'Failed to fetch VTO looks')
  },

  /**
   * Update VTO look's current generation (called after generation completes)
   */
  updateCurrentGeneration: async (
    lookId: string,
    generationId: string
  ): Promise<void> => {
    const { error } = await supabase
      .from('vto_looks')
      .update({ current_generation_id: generationId })
      .eq('id', lookId)

    if (error) {
      throw new Error(
        `Failed to update VTO look current generation: ${error.message}`,
        { cause: error }
      )
    }
  },

  /**
   * Update VTO look metadata (name)
   */
  update: async (lookId: string, input: { name?: string }): Promise<void> => {
    const { error } = await supabase
      .from('vto_looks')
      .update({
        ...(input.name !== undefined && { name: input.name }),
      })
      .eq('id', lookId)

    if (error) {
      throw new Error(`Failed to update VTO look: ${error.message}`, {
        cause: error,
      })
    }
  },

  /**
   * Update VTO look approval status
   */
  updateApproval: async (
    lookId: string,
    input: {
      approvalStatus: 'approved' | 'denied' | null
      approvalComment: string | null
      approvedBy: string | null
    }
  ): Promise<void> => {
    if (!lookId || lookId.trim() === '') {
      throw new Error('Invalid lookId: cannot update approval without valid ID')
    }

    const { error } = await supabase
      .from('vto_looks')
      .update({
        approval_status: input.approvalStatus,
        approval_comment: input.approvalComment,
        approved_by: input.approvedBy,
        approved_at: input.approvalStatus ? new Date().toISOString() : null,
      })
      .eq('id', lookId)

    if (error) {
      throw new Error(`Failed to update VTO look approval: ${error.message}`, {
        cause: error,
      })
    }
  },

  /**
   * Bulk update VTO look approval status
   */
  bulkUpdateApproval: async (
    lookIds: string[],
    input: {
      approvalStatus: 'approved' | 'denied' | null
      approvedBy: string | null
    }
  ): Promise<void> => {
    if (lookIds.length === 0) {
      return
    }

    const { error } = await supabase
      .from('vto_looks')
      .update({
        approval_status: input.approvalStatus,
        approval_comment: null,
        approved_by: input.approvedBy,
        approved_at: input.approvalStatus ? new Date().toISOString() : null,
      })
      .in('id', lookIds)

    if (error) {
      throw new Error(`Failed to bulk update approval: ${error.message}`, {
        cause: error,
      })
    }
  },

  /**
   * Bulk update approval status for all VTO looks matching workspace + filters.
   * Same two-path logic as deleteAllByWorkspace.
   */
  bulkUpdateApprovalByWorkspace: async (
    workspaceId: string,
    filters: VTOLookFilters | undefined,
    input: {
      approvalStatus: 'approved' | 'denied' | null
      approvedBy: string | null
    },
    excludeIds?: string[]
  ): Promise<void> => {
    if (!workspaceId || workspaceId.trim() === '') {
      return
    }

    const updatePayload = {
      approval_status: input.approvalStatus,
      approval_comment: null,
      approved_by: input.approvedBy,
      approved_at: input.approvalStatus ? new Date().toISOString() : null,
    }

    // An update cannot be scoped through the catalogue join, so it goes by id.
    if (hasProductFilters(filters)) {
      const excluded = new Set(excludeIds)
      const lookIds = (
        await vtoLook.fetchIdsByWorkspace(workspaceId, filters)
      ).filter((id) => !excluded.has(id))
      await updateLooksById(
        lookIds,
        updatePayload,
        'Failed to bulk update approval'
      )
    } else {
      let baseQuery = supabase
        .from('vto_looks')
        .update(updatePayload)
        .eq('workspace_id', workspaceId)
        .is('deleted_at', null)

      baseQuery = applyBasicFilters(baseQuery, filters)

      if (excludeIds && excludeIds.length > 0) {
        baseQuery = baseQuery.not('id', 'in', `(${excludeIds.join(',')})`)
      }

      const { error } = await baseQuery

      if (error) {
        throw new Error(`Failed to bulk update approval: ${error.message}`, {
          cause: error,
        })
      }
    }
  },

  /**
   * Soft delete a VTO look
   */
  delete: async (lookId: string): Promise<void> => {
    if (!lookId || lookId.trim() === '') {
      throw new Error('Invalid lookId: cannot delete VTO look without valid ID')
    }

    const { error } = await supabase
      .from('vto_looks')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', lookId)

    if (error) {
      throw new Error(`Failed to delete VTO look: ${error.message}`, {
        cause: error,
      })
    }
  },

  /**
   * Soft delete multiple VTO looks by IDs
   */
  deleteMany: async (lookIds: string[]): Promise<void> => {
    if (!lookIds.length) {
      return
    }

    const { error } = await supabase
      .from('vto_looks')
      .update({ deleted_at: new Date().toISOString() })
      .in('id', lookIds)

    if (error) {
      throw new Error(`Failed to delete VTO looks: ${error.message}`, {
        cause: error,
      })
    }
  },

  deleteAllByWorkspace: async (
    workspaceId: string,
    filters?: VTOLookFilters,
    excludeIds?: string[]
  ): Promise<void> => {
    if (!workspaceId || workspaceId.trim() === '') {
      return
    }

    // An update cannot be scoped through the catalogue join, so it goes by id.
    if (hasProductFilters(filters)) {
      const excluded = new Set(excludeIds)
      const lookIds = (
        await vtoLook.fetchIdsByWorkspace(workspaceId, filters)
      ).filter((id) => !excluded.has(id))
      await updateLooksById(
        lookIds,
        { deleted_at: new Date().toISOString() },
        'Failed to delete VTO looks'
      )
    } else {
      let baseQuery = supabase
        .from('vto_looks')
        .update({ deleted_at: new Date().toISOString() })
        .eq('workspace_id', workspaceId)
        .is('deleted_at', null)

      baseQuery = applyBasicFilters(baseQuery, filters)

      if (excludeIds && excludeIds.length > 0) {
        baseQuery = baseQuery.not('id', 'in', `(${excludeIds.join(',')})`)
      }

      const { error } = await baseQuery

      if (error) {
        throw new Error(`Failed to delete VTO looks: ${error.message}`, {
          cause: error,
        })
      }
    }
  },
}

/**
 * Find a VTO look in any workspace infinite query cache.
 * For use in route loaders (non-hook context).
 */
export function findCachedVTOLook(
  queryClient: QueryClient,
  workspaceId: string,
  lookId: string
): VTOLook | null {
  const queries = queryClient.getQueriesData<InfiniteData<VTOLook[]>>({
    queryKey: ['vto-looks', workspaceId],
    exact: false,
  })

  for (const [, data] of queries) {
    if (!Array.isArray(data?.pages)) {
      continue
    }
    const found = data.pages.flat().find((l) => l.id === lookId)
    if (found) {
      return found
    }
  }
  return null
}
